import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { VeriskAuthService } from 'src/app/services/Authetication/verisk-auth.service';
import { LoggingService } from 'src/app/services/logging.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class VeriskAuthGuard {
  // Verisk SSO authorization endpoint - should be configured in environment
  private readonly VERISK_SSO_URL = environment.veriskSSOUrl || 'https://sso.verisk.com/authorize';
  private readonly REDIRECT_URI = '/requests/request-details';

  constructor(
    private router: Router,
    private veriskAuthService: VeriskAuthService,
    private logger: LoggingService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const parsedUrl = this.router.parseUrl(state.url);
    const identifier =
      route.queryParams['identifier'] ||
      route.queryParams['iss'] ||
      parsedUrl.queryParams['identifier'] ||
      parsedUrl.queryParams['iss'];

    const loginData =
      route.queryParams['loginData'] ||

      parsedUrl.queryParams['loginData'];

    // ClaimSearch partner link (identifier): always re-run SSO — do not trust loginData or local session.

    if (identifier) {

      this.veriskAuthService.clearVeriskData();

      try {

        localStorage.removeItem('currentUserData');

      } catch (_) {}

      return this.redirectToVeriskSSO(route, state.url);

    }

    // Post-SSO callback: loginData only (no identifier in URL).

    if (loginData) {

      this.persistSessionFromLoginData(loginData);
      return true;
    }

    // Verisk user with unauthorized=1: allow through so request-details component can show message and redirect to clean URL
    const unauthorized = route.queryParams['unauthorized'] ?? this.router.parseUrl(state.url).queryParams['unauthorized'];
    if ((unauthorized === '1' || unauthorized === 1) && this.veriskAuthService.getVeriskToken()) {
      return true;
    }

    // URL has Error param (e.g. "You are not authorized to access this report."): allow through so request-details can show message and stay on page
    const errorParam = route.queryParams['Error'] ?? this.router.parseUrl(state.url).queryParams['Error'];
    if (errorParam) {
      return true;
    }

    const token = this.veriskAuthService.getVeriskToken();
    const hasLocalSession = !!(
      localStorage.getItem('currentUserData') &&
      JSON.parse(localStorage.getItem('currentUserData') || '{}')?.accessToken
    );

    this.logger.logGuardCheck(
      state.url,
      !!token,
      hasLocalSession,
      'VeriskAuthGuard'
    );

    // Internal navigation (no identifier) — reuse app session when present.

    if (hasLocalSession) {

      return true;

    }

    if (!token) {

      return this.redirectToVeriskSSO(route, state.url);

    }

    // Validate the Verisk token with backend
    return this.veriskAuthService.validateVeriskUser(token).pipe(
      map((response: any) => {
        if (response && response.statusCode === 200 && response.data) {
          this.logger.logTokenValidation(true, state.url, true);
          return true;
        } else {
          this.logger.logTokenValidation(false, state.url, true);
          return this.redirectToVeriskSSO(route, state.url);
        }
      }),
      catchError((error: any) => {
        this.logger.logTokenValidation(
          false,
          state.url,
          true,
          error?.message || `HTTP ${error?.status}`
        );
        return of(this.redirectToVeriskSSO(route, state.url));
      })
    );
  }

  /**
   * Persist token and user from loginData so layout/API calls get Authorization before components run.
   */
  private persistSessionFromLoginData(loginData: string): void {
    try {
      const decoded = JSON.parse(decodeURIComponent(loginData));
      console.log('decoded', decoded);
      if (decoded && typeof decoded === 'object') {
        localStorage.removeItem('SuperAdminUserName');
        localStorage.removeItem('SuperAdminUserId');
        localStorage.removeItem('SuperAdminUserRole');
        localStorage.removeItem('flgIsLoggedInAsUser');
        const userData = { ...decoded, isVeriskUser: true };
        console.log('userData', userData);
        localStorage.setItem('currentUserData', JSON.stringify(userData));
        if (decoded.accessToken) {
          console.log('decoded.accessToken', decoded.accessToken);
          localStorage.setItem('veriskToken', decoded.accessToken);
          console.log('localStorage.setItem veriskToken', decoded.accessToken);
          localStorage.setItem('veriskUserData', JSON.stringify(decoded));
        }
      }
    } catch (_) {
      // ignore
    }
  }

  /**
   * Verisk SSO is only used when the user arrives with an identifier URL (iss or identifier query param).
   * - If URL has 'iss' or 'identifier': calls /api/Entry/resolve to initiate Verisk OIDC flow.
   * - If no identifier: redirect to app login (do not trigger Verisk SSO).
   */
  private redirectToVeriskSSO(route: ActivatedRouteSnapshot, returnUrl: string): UrlTree {
    const parsed = this.router.parseUrl(returnUrl);
    const windowParams = new URLSearchParams(window.location.search);
    console.log(' parsed.queryParams', parsed.queryParams, route.queryParams, windowParams);
    const identifier =
      parsed.queryParams['identifier'] || route.queryParams['identifier'] ||
      parsed.queryParams['iss'] || route.queryParams['iss'] ||
      windowParams.get('identifier') || windowParams.get('iss');
    const subscriptionId =
      parsed.queryParams['subscriptionId'] || route.queryParams['subscriptionId'] ||
      windowParams.get('subscriptionId') || undefined;

    if (identifier) {
      const fullReturnUrl = window.location.origin + (returnUrl.startsWith('/') ? returnUrl : '/' + returnUrl);
      let apiUrl = `${environment.WebApiURL}api/Entry/resolve?identifier=${encodeURIComponent(identifier)}&returnUrl=${encodeURIComponent(fullReturnUrl)}`;
      if (subscriptionId) {
        apiUrl += `&subscriptionId=${encodeURIComponent(subscriptionId)}`;
      }
      try {
        localStorage.setItem('veriskEntryResolveUrl', apiUrl);
      } catch (_) {}
      this.logger.logIdentifierDetected(returnUrl, identifier, fullReturnUrl, 'VeriskAuthGuard');
      this.logger.logSsoLoginRedirect(returnUrl, identifier, fullReturnUrl, apiUrl);
      window.location.href = apiUrl;
      console.log('window.location.href', window.location.href);
      return this.router.parseUrl('/login'); // Return value not used - browser is navigating away
    }

    // No identifier: do not use Verisk SSO — redirect to app login
    this.logger.logNoSessionRedirect(returnUrl, false);
    console.log('this.router.createUrlTree', this.router.createUrlTree(['/login']));
    return this.router.createUrlTree(['/login']);
  }
}